import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import skylarqLogo from '../../assets/skylarq_logo_small.png';

// VITE_BACKEND_URL already includes the /api/v1 suffix in all envs; falls back
// to /api/v1 (same-origin) when unset so the prod build works behind a proxy.
const API_BASE = import.meta.env.VITE_BACKEND_URL || '/api/v1';

export const UnsubscribePage: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState('');

  const handleUnsubscribe = async () => {
    if (!token) return;
    setStatus('loading');
    try {
      const res = await fetch(`${API_BASE}/campaigns/public/unsubscribe/${token}`, {
        method: 'POST',
      });
      if (res.ok) {
        setStatus('success');
      } else {
        setErrorMsg('Something went wrong. Please try again.');
        setStatus('error');
      }
    } catch {
      setErrorMsg('Network error. Please check your connection and try again.');
      setStatus('error');
    }
  };

  return (
    <div className="min-h-screen bg-neutral-50 flex flex-col items-center justify-center px-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="flex justify-center mb-8">
          <img src={skylarqLogo} alt="Skylarq" className="h-8" />
        </div>

        {/* Card */}
        <div className="bg-white rounded-xl shadow-sm border border-neutral-200 p-8 text-center">
          {status === 'success' ? (
            <>
              <div className="w-12 h-12 rounded-full bg-green-50 flex items-center justify-center mx-auto mb-4">
                <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                </svg>
              </div>
              <h1 className="text-lg font-semibold text-neutral-900 mb-2">
                You've been unsubscribed
              </h1>
              <p className="text-sm text-neutral-500">
                You won't receive any more emails from this sender. This may take a few minutes to take effect.
              </p>
            </>
          ) : status === 'error' ? (
            <>
              <div className="w-12 h-12 rounded-full bg-red-50 flex items-center justify-center mx-auto mb-4">
                <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
                </svg>
              </div>
              <h1 className="text-lg font-semibold text-neutral-900 mb-2">
                Something went wrong
              </h1>
              <p className="text-sm text-neutral-500 mb-4">{errorMsg}</p>
              <button
                onClick={handleUnsubscribe}
                className="px-5 py-2.5 bg-neutral-900 text-white text-sm font-medium rounded-lg hover:bg-neutral-800 transition-colors"
              >
                Try again
              </button>
            </>
          ) : (
            <>
              <div className="w-12 h-12 rounded-full bg-neutral-100 flex items-center justify-center mx-auto mb-4">
                <svg className="w-6 h-6 text-neutral-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M21.75 9v.906a2.25 2.25 0 01-1.183 1.981l-6.478 3.488M2.25 9v.906a2.25 2.25 0 001.183 1.981l6.478 3.488m8.839 2.51l-4.66-2.51m0 0l-1.023-.55a2.25 2.25 0 00-2.134 0l-1.022.55m0 0l-4.661 2.51m16.5 1.615a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V8.844a2.25 2.25 0 011.183-1.98l7.5-4.04a2.25 2.25 0 012.134 0l7.5 4.04a2.25 2.25 0 011.183 1.98V19.5z" />
                </svg>
              </div>
              <h1 className="text-lg font-semibold text-neutral-900 mb-2">
                Unsubscribe from emails
              </h1>
              <p className="text-sm text-neutral-500 mb-6">
                Click below to stop receiving emails from this sender. You can always re-subscribe later.
              </p>
              <button
                onClick={handleUnsubscribe}
                disabled={status === 'loading'}
                className="px-6 py-2.5 bg-red-600 text-white text-sm font-medium rounded-lg hover:bg-red-700 transition-colors disabled:opacity-60"
              >
                {status === 'loading' ? 'Processing...' : 'Unsubscribe'}
              </button>
            </>
          )}
        </div>

        {/* Footer */}
        <p className="text-center text-xs text-neutral-400 mt-6">
          Powered by Skylarq
        </p>
      </div>
    </div>
  );
};
